import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { 
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  doc,
  setDoc
} from 'firebase/firestore';
import { getPerformance } from 'firebase/performance';
import { getStorage } from 'firebase/storage';
import { getAnalytics, isSupported } from 'firebase/analytics';

// Firebase config from environment (Create React App injects REACT_APP_* at build time)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Auth with custom settings
export const auth = getAuth(app);
auth.useDeviceLanguage();
auth.settings = {
  appVerificationDisabledForTesting: false, // Enable proper verification
  popupRedirectResolver: undefined // Let Firebase handle the resolver
};

// Initialize Firestore with persistent cache
export const db = initializeFirestore(app, {
  cache: persistentLocalCache({
    tabManager: persistentMultipleTabManager()
  })
});

// Initialize Storage
export const storage = getStorage(app);

// Initialize Performance Monitoring
export const perf = getPerformance(app);

// Initialize Analytics (only if supported)
let analytics = null;
const initializeAnalytics = async () => {
  try {
    const supported = await isSupported();
    if (supported) {
      analytics = getAnalytics(app);
      console.log('✅ Firebase Analytics initialized successfully');
      
      // Enable debug mode in development
      if (process.env.NODE_ENV === 'development') {
        // Enable analytics debug mode for development
        window.gtag && window.gtag('config', firebaseConfig.measurementId, {
          debug_mode: true,
          send_page_view: false // We'll handle page views manually
        });
      }
    } else {
      console.warn('⚠️ Firebase Analytics not supported in this environment');
    }
  } catch (error) {
    console.error('❌ Error initializing Analytics:', error);
  }
};

// Initialize analytics
initializeAnalytics();

export { analytics };

// Log initialization status (no config values to avoid exposing in console)
if (process.env.NODE_ENV === 'development') {
  console.log('🔥 Firebase initialized:', {
    isAppInitialized: !!app,
    isAuthInitialized: !!auth,
    isDbInitialized: !!db,
    isStorageInitialized: !!storage
  });
}

// Function to initialize admin user
export const initializeAdminUser = async (adminEmail) => {
  try {
    await setDoc(doc(db, 'authorized_users', adminEmail.toLowerCase()), {
      role: 'admin',
      addedAt: new Date(),
      addedBy: 'system'
    }, { merge: true });
    console.log('Admin user initialized successfully');
  } catch (error) {
    console.error('Error initializing admin user:', error);
    throw error;
  }
};

export default app;